import { DateTime } from 'luxon';

import { AvailabilityMode, IAvailabilityFilters } from './types';

const parseIdParam = (value: string | null): number | undefined => {
  if (!value) {
    return undefined;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const parseDateParam = (value: string | null): Date | undefined => {
  if (!value) {
    return undefined;
  }
  const parsed = DateTime.fromISO(value, { zone: 'utc' });
  return parsed.isValid ? parsed.toJSDate() : undefined;
};

export const parseFiltersFromSearch = (search: string): IAvailabilityFilters => {
  const params = new URLSearchParams(search);
  const filters: IAvailabilityFilters = {};

  const regionID = parseIdParam(params.get('region_id'));
  if (regionID !== undefined) {
    filters.region_id = regionID;
  }

  const userID = parseIdParam(params.get('user_id'));
  if (userID !== undefined) {
    filters.user_id = userID;
  }

  const mode = params.get('mode');
  if (mode === 'daily' || mode === 'management') {
    filters.mode = mode;
  }

  const date = parseDateParam(params.get('date') ?? params.get('week'));
  if (date) {
    filters.date = date;
  }

  return filters;
};

export const cleanupFilters = (filters: IAvailabilityFilters): Record<string, string | number> => {
  const results: Record<string, string | number> = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value instanceof Date) {
      results[key] = DateTime.fromJSDate(value, { zone: 'utc' }).toFormat('yyyy-MM-dd');
      return;
    }
    if (value !== undefined && value !== null) {
      if (typeof value === 'string' && value.length === 0) {
        return;
      }
      results[key] = value as string | number;
    }
  });

  return results;
};

export const syncFiltersToUrl = (filters: IAvailabilityFilters) => {
  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams();

  Object.entries(cleanupFilters(filters)).forEach(([key, value]) => {
    searchParams.set(key, String(value));
  });

  url.search = searchParams.toString();
  window.history.replaceState({}, '', url.toString());
};

export const formatDateInputValue = (date: Date | undefined): string => {
  if (!date) {
    return '';
  }
  return DateTime.fromJSDate(date, { zone: 'utc' }).toFormat('yyyy-MM-dd');
};

export const parseDateInputValue = (value: string): Date | undefined => {
  if (!value) {
    return undefined;
  }
  const parsed = DateTime.fromISO(value, { zone: 'utc' });
  return parsed.isValid ? parsed.toJSDate() : undefined;
};

export const dateInputBounds = () => {
  const now = DateTime.utc();
  return {
    min: now.minus({ years: 5 }).toFormat('yyyy-MM-dd'),
    max: now.plus({ years: 5 }).toFormat('yyyy-MM-dd'),
  };
};

export const isDailyFiltering = (filters: IAvailabilityFilters): boolean =>
  filters.mode === 'daily' && !!filters.region_id && !!filters.date;

export const isManagementFiltering = (filters: IAvailabilityFilters): boolean =>
  filters.mode === 'management' && !!filters.user_id;

export const usersForRegion = <T extends { region_ids: number[] }>(users: T[], regionID: number | undefined): T[] => {
  if (!regionID) {
    return users;
  }
  return users.filter((user) => user.region_ids.includes(regionID));
};

export const isMode = (value: string): value is AvailabilityMode => value === 'daily' || value === 'management';
